/**
 * WeekGeneratorSheet - Interface de génération de planning ADHD-friendly
 *
 * Conçu pour les personnes avec TDAH/troubles psy:
 * - Interface simple et non-submersive
 * - Étapes visuelles claires
 * - Feedback immédiat et encourageant
 * - Options de personnalisation sans surcharge
 */

import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Calendar, MapPin, Heart, Zap, Coffee,
  ChevronRight, ChevronLeft, Check, Loader2,
  User, Home, Dumbbell, ShoppingCart, Brain, Star
} from 'lucide-react';
import { BottomSheet } from '../ui/BottomSheet';

// Catégories d'activités avec icônes adaptées ADHD
const ACTIVITY_TYPES = [
  { id: 'sport', icon: Dumbbell, label: 'Sport', color: '#22c55e', emoji: '🏃' },
  { id: 'courses', icon: ShoppingCart, label: 'Courses', color: '#f59e0b', emoji: '🛒' },
  { id: 'social', icon: User, label: 'Social', color: '#ec4899', emoji: '👥' },
  { id: 'detente', icon: Coffee, label: 'Détente', color: '#8b5cf6', emoji: '☕' },
  { id: 'sante', icon: Heart, label: 'Santé', color: '#ef4444', emoji: '💊' },
  { id: 'menage', icon: Home, label: 'Ménage', color: '#64748b', emoji: '🧹' },
];

// Niveaux d'énergie avec descriptions ADHD-friendly
const ENERGY_LEVELS = [
  { value: 1, label: 'Très bas', emoji: '😴', desc: 'Journée douce, peu d\'activités' },
  { value: 2, label: 'Bas', emoji: '🌙', desc: 'Tâches légères seulement' },
  { value: 3, label: 'Normal', emoji: '⚡', desc: 'Mix équilibré' },
  { value: 4, label: 'Élevé', emoji: '🔥', desc: 'Journée productive' },
];

// Messages d'encouragement rotatifs
const ENCOURAGEMENTS = [
  "Tu vas créer une semaine sur mesure ! 🌟",
  "Chaque petit pas compte, tu gères ! 💪",
  "Ton planning, tes règles ! ✨",
  "On y va doucement, à ton rythme 🌸",
  "Prêt(e) à organiser ta semaine ? 🎯"
];

/**
 * Composant principal du générateur de semaine
 */
export function WeekGeneratorSheet({
  isOpen,
  onClose,
  onGenerate,
  userProfile,
  onUpdateProfile,
  isGenerating = false,
  weekDates = [],
  onOpenProfile, // Pour éditer le profil
}) {
  // État du wizard
  const [step, setStep] = useState(0);
  const [selectedDays, setSelectedDays] = useState([]);
  const [energyLevel, setEnergyLevel] = useState(3);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [customRequests, setCustomRequests] = useState('');
  const [encouragement] = useState(() =>
    ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)]
  );

  // Réinitialiser à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      // Sélectionner tous les jours par défaut
      setSelectedDays(weekDates.map((_, i) => i));
    }
  }, [isOpen, weekDates]);

  // Navigation
  const nextStep = useCallback(() => {
    setStep(s => Math.min(s + 1, 2));
  }, []);

  const prevStep = useCallback(() => {
    setStep(s => Math.max(s - 1, 0));
  }, []);

  // Toggle jour
  const toggleDay = useCallback((index) => {
    setSelectedDays(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  }, []);

  // Toggle activité
  const toggleActivity = useCallback((id) => {
    setSelectedActivities(prev =>
      prev.includes(id)
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  }, []);

  // Générer
  const handleGenerate = useCallback(() => {
    const options = {
      selectedDays: selectedDays.map(i => weekDates[i]),
      energyLevel,
      requestedActivities: selectedActivities.map(id => {
        const act = ACTIVITY_TYPES.find(a => a.id === id);
        return { category: id, label: act?.label };
      }),
      customRequests,
    };
    onGenerate(options);
  }, [selectedDays, weekDates, energyLevel, selectedActivities, customRequests, onGenerate]);

  // Jours de la semaine formatés
  const formattedDays = weekDates.map((date, i) => {
    const d = new Date(date);
    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const isToday = new Date().toDateString() === d.toDateString();
    return {
      index: i,
      name: dayNames[d.getDay()],
      date: d.getDate(),
      isWeekend: d.getDay() === 0 || d.getDay() === 6,
      isToday
    };
  });

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      height="85%"
    >
      <div className="flex flex-col h-full">
        {/* Header avec progression */}
        <div className="flex-shrink-0 px-4 pt-2 pb-4 border-b border-zinc-800">
          {/* Indicateur de progression */}
          <div className="flex items-center gap-2 mb-3">
            {[0, 1, 2].map(s => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all ${
                  s <= step ? 'bg-blue-500' : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>

          {/* Titre et encouragement */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-zinc-100 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-yellow-400" />
                Générer ma semaine
              </h2>
              <p className="text-sm text-zinc-400 mt-0.5">{encouragement}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-zinc-400 hover:text-zinc-200"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Contenu des étapes */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <AnimatePresence mode="wait">
            {/* Étape 0: Sélection des jours */}
            {step === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 text-zinc-300">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  <span className="font-medium">Quels jours planifier ?</span>
                </div>

                <p className="text-sm text-zinc-500">
                  Sélectionne les jours pour lesquels tu veux générer un planning.
                  Tu peux toujours modifier après !
                </p>

                {/* Grille des jours */}
                <div className="grid grid-cols-7 gap-2">
                  {formattedDays.map(day => (
                    <button
                      key={day.index}
                      onClick={() => toggleDay(day.index)}
                      className={`
                        p-3 rounded-lg text-center transition-all
                        ${selectedDays.includes(day.index)
                          ? 'bg-blue-500/20 border-2 border-blue-500 text-blue-400'
                          : 'bg-zinc-800/50 border-2 border-transparent text-zinc-400 hover:bg-zinc-800'
                        }
                        ${day.isToday ? 'ring-2 ring-yellow-500/50' : ''}
                      `}
                    >
                      <div className="text-xs font-medium">{day.name}</div>
                      <div className="text-lg font-bold">{day.date}</div>
                      {day.isWeekend && (
                        <div className="text-[10px] text-zinc-500">WE</div>
                      )}
                    </button>
                  ))}
                </div>

                {/* Raccourcis */}
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setSelectedDays([0, 1, 2, 3, 4, 5, 6])}
                    className="px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 rounded-full hover:bg-zinc-700"
                  >
                    Toute la semaine
                  </button>
                  <button
                    onClick={() => setSelectedDays([0, 1, 2, 3, 4])}
                    className="px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 rounded-full hover:bg-zinc-700"
                  >
                    Semaine travail
                  </button>
                  <button
                    onClick={() => setSelectedDays([5, 6])}
                    className="px-3 py-1.5 text-xs bg-zinc-800 text-zinc-300 rounded-full hover:bg-zinc-700"
                  >
                    Weekend
                  </button>
                </div>
              </motion.div>
            )}

            {/* Étape 1: Niveau d'énergie */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 text-zinc-300">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  <span className="font-medium">Comment te sens-tu cette semaine ?</span>
                </div>

                <p className="text-sm text-zinc-500">
                  Pas de jugement ! On adapte le planning à ton niveau d'énergie actuel.
                </p>

                {/* Niveaux d'énergie */}
                <div className="space-y-2">
                  {ENERGY_LEVELS.map(level => (
                    <button
                      key={level.value}
                      onClick={() => setEnergyLevel(level.value)}
                      className={`
                        w-full p-4 rounded-lg text-left transition-all flex items-center gap-4
                        ${energyLevel === level.value
                          ? 'bg-blue-500/20 border-2 border-blue-500'
                          : 'bg-zinc-800/50 border-2 border-transparent hover:bg-zinc-800'
                        }
                      `}
                    >
                      <span className="text-2xl">{level.emoji}</span>
                      <div className="flex-1">
                        <div className={`font-medium ${
                          energyLevel === level.value ? 'text-blue-400' : 'text-zinc-300'
                        }`}>
                          {level.label}
                        </div>
                        <div className="text-sm text-zinc-500">{level.desc}</div>
                      </div>
                      {energyLevel === level.value && (
                        <Check className="w-5 h-5 text-blue-400" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Message d'encouragement ADHD */}
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 mt-4">
                  <div className="flex items-start gap-2">
                    <Brain className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-purple-300">
                      <strong>Rappel bienveillant :</strong> Les jours de basse énergie ne sont pas des échecs.
                      C'est ton corps qui te dit de ralentir. On adapte, pas de pression !
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Étape 2: Activités souhaitées */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="flex items-center gap-2 text-zinc-300">
                  <Star className="w-5 h-5 text-amber-400" />
                  <span className="font-medium">Qu'est-ce que tu aimerais faire ?</span>
                </div>

                <p className="text-sm text-zinc-500">
                  Sélectionne les types d'activités à inclure. L'IA ajoutera les détails et les lieux !
                </p>

                {/* Grille d'activités */}
                <div className="grid grid-cols-2 gap-3">
                  {ACTIVITY_TYPES.map(activity => {
                    const Icon = activity.icon;
                    const isSelected = selectedActivities.includes(activity.id);
                    return (
                      <button
                        key={activity.id}
                        onClick={() => toggleActivity(activity.id)}
                        className={`
                          p-4 rounded-lg text-left transition-all flex items-center gap-3
                          ${isSelected
                            ? 'bg-blue-500/20 border-2 border-blue-500'
                            : 'bg-zinc-800/50 border-2 border-transparent hover:bg-zinc-800'
                          }
                        `}
                      >
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${activity.color}20` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: activity.color }} />
                        </div>
                        <div>
                          <div className={`font-medium ${isSelected ? 'text-blue-400' : 'text-zinc-300'}`}>
                            {activity.emoji} {activity.label}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Zone de texte pour demandes custom */}
                <div className="pt-4">
                  <label className="text-sm text-zinc-400 block mb-2">
                    Autre chose à ajouter ? (optionnel)
                  </label>
                  <textarea
                    value={customRequests}
                    onChange={(e) => setCustomRequests(e.target.value)}
                    placeholder="Ex: RDV médecin mardi à 14h, footing au parc Montsouris..."
                    className="w-full p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 text-sm resize-none focus:outline-none focus:border-blue-500"
                    rows={3}
                  />
                </div>

                {/* Profil utilisateur résumé */}
                {userProfile && (
                  <div className="bg-zinc-800/30 rounded-lg p-3 mt-2">
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {userProfile.city || 'Localisation non définie'}
                        {userProfile.hobbies?.length > 0 && (
                          <span className="ml-2">
                            • {userProfile.hobbies.slice(0, 2).join(', ')}
                          </span>
                        )}
                      </span>
                    </div>
                    <button
                      onClick={() => onOpenProfile?.()}
                      className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                    >
                      Modifier mon profil →
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer avec navigation */}
        <div className="flex-shrink-0 px-4 py-4 border-t border-zinc-800 bg-zinc-900/80">
          <div className="flex items-center gap-3">
            {/* Bouton retour */}
            {step > 0 ? (
              <button
                onClick={prevStep}
                className="px-4 py-2.5 bg-zinc-800 text-zinc-300 rounded-lg hover:bg-zinc-700 flex items-center gap-2"
              >
                <ChevronLeft className="w-4 h-4" />
                Retour
              </button>
            ) : (
              <button
                onClick={onClose}
                className="px-4 py-2.5 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700"
              >
                Annuler
              </button>
            )}

            {/* Bouton principal */}
            <button
              onClick={step < 2 ? nextStep : handleGenerate}
              disabled={isGenerating || (step === 0 && selectedDays.length === 0)}
              className={`
                flex-1 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all
                ${isGenerating
                  ? 'bg-blue-500/50 text-blue-200 cursor-wait'
                  : step < 2
                    ? 'bg-blue-600 text-white hover:bg-blue-500'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500'
                }
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Génération en cours...
                </>
              ) : step < 2 ? (
                <>
                  Continuer
                  <ChevronRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Générer ma semaine
                </>
              )}
            </button>
          </div>

          {/* Info ADHD */}
          <p className="text-xs text-center text-zinc-500 mt-3">
            💡 Tu pourras modifier chaque événement après la génération
          </p>
        </div>
      </div>
    </BottomSheet>
  );
}

export default WeekGeneratorSheet;
